import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Sidebar from '@/components/Sidebar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { Plus, Trash2, ArrowLeft } from 'lucide-react';
import { Switch } from '@/components/ui/switch';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ManageFields() {
  const [fields, setFields] = useState([]);
  const [fieldName, setFieldName] = useState('');
  const [fieldType, setFieldType] = useState('text');
  const [isRequired, setIsRequired] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchFields();
  }, []);

  const fetchFields = async () => {
    try {
      const response = await axios.get(`${API}/fields`);
      setFields(response.data);
    } catch (error) {
      toast.error('Failed to fetch fields');
    }
  };

  const handleAddField = async (e) => {
    e.preventDefault();
    if (!fieldName.trim()) {
      toast.error('Field name is required');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/fields`,
        {
          field_name: fieldName,
          field_type: fieldType,
          is_required: isRequired,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      toast.success('Field added successfully!');
      setFieldName('');
      setFieldType('text');
      setIsRequired(false);
      fetchFields();
    } catch (error) {
      toast.error('Failed to add field');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteField = async (fieldId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/fields/${fieldId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Field deleted successfully');
      fetchFields();
    } catch (error) {
      toast.error('Failed to delete field');
    }
  };

  return (
    <div className="flex min-h-screen bg-zinc-950" data-testid="manage-fields-page">
      <Sidebar />
      <main className="flex-1 p-8">
        <div className="max-w-4xl mx-auto">
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="mb-6 text-zinc-400 hover:text-white hover:bg-zinc-800"
            data-testid="back-button"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>

          <h1 className="text-4xl font-bold text-white mb-2" data-testid="manage-fields-title">Manage Custom Fields</h1>
          <p className="text-zinc-400 mb-8" data-testid="manage-fields-description">
            Create and manage custom fields that will appear in the car forms
          </p>

          <Card className="border-zinc-800 bg-zinc-900/50 mb-8">
            <CardContent className="p-6">
              <h2 className="text-xl font-semibold text-white mb-6" data-testid="add-field-title">Add New Field</h2>
              <form onSubmit={handleAddField} className="space-y-4" data-testid="add-field-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="fieldName" className="text-zinc-300">Field Name *</Label>
                    <Input
                      id="fieldName"
                      value={fieldName}
                      onChange={(e) => setFieldName(e.target.value)}
                      placeholder="e.g., Mileage, Engine Type"
                      className="bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500 focus:border-blue-500"
                      data-testid="field-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="fieldType" className="text-zinc-300">Field Type *</Label>
                    <Select value={fieldType} onValueChange={setFieldType}>
                      <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white" data-testid="field-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-800 border-zinc-700">
                        <SelectItem value="text" className="text-white hover:bg-zinc-700" data-testid="field-type-text">Text</SelectItem>
                        <SelectItem value="number" className="text-white hover:bg-zinc-700" data-testid="field-type-number">Number</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="isRequired"
                    checked={isRequired}
                    onCheckedChange={setIsRequired}
                    data-testid="is-required-switch"
                  />
                  <Label htmlFor="isRequired" className="text-zinc-300 cursor-pointer">Required field</Label>
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white"
                  data-testid="add-field-button"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {loading ? 'Adding...' : 'Add Field'}
                </Button>
              </form>
            </CardContent>
          </Card>

          <Card className="border-zinc-800 bg-zinc-900/50">
            <CardContent className="p-6">
              <h2 className="text-xl font-semibold text-white mb-6" data-testid="existing-fields-title">Existing Fields</h2>
              {fields.length === 0 ? (
                <p className="text-zinc-400 text-center py-8" data-testid="no-fields-message">No custom fields created yet</p>
              ) : (
                <div className="space-y-3" data-testid="fields-list">
                  {fields.map((field) => (
                    <div
                      key={field.id}
                      className="flex items-center justify-between p-4 rounded-lg bg-zinc-800/50 border border-zinc-800 hover:border-zinc-700 transition-colors"
                      data-testid={`field-item-${field.id}`}
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <h3 className="text-white font-semibold" data-testid={`field-name-${field.id}`}>{field.field_name}</h3>
                          <span className="text-xs px-2 py-1 rounded bg-blue-600/20 text-blue-400 border border-blue-600/30" data-testid={`field-type-${field.id}`}>
                            {field.field_type}
                          </span>
                          {field.is_required && (
                            <span className="text-xs px-2 py-1 rounded bg-red-600/20 text-red-400 border border-red-600/30" data-testid={`field-required-${field.id}`}>
                              Required
                            </span>
                          )}
                        </div>
                      </div>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-400 hover:text-red-300 hover:bg-red-950"
                            data-testid={`delete-field-button-${field.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="bg-zinc-900 border-zinc-800" data-testid={`delete-field-dialog-${field.id}`}>
                          <AlertDialogHeader>
                            <AlertDialogTitle className="text-white">Delete Field?</AlertDialogTitle>
                            <AlertDialogDescription className="text-zinc-400">
                              This will delete the field and all associated values from existing cars. This action cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel className="bg-zinc-800 text-white border-zinc-700 hover:bg-zinc-700" data-testid={`cancel-delete-field-${field.id}`}>
                              Cancel
                            </AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleDeleteField(field.id)}
                              className="bg-red-600 hover:bg-red-700 text-white"
                              data-testid={`confirm-delete-field-${field.id}`}
                            >
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
